import { useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase.js';
import PublicFonts from '../components/PublicFonts.jsx';
import './landing.css';

// Public page: customer follows their invite URL here.
// We never show the invite token — it stays in the URL and is sent only to the function.

export default function AcceptInvite() {
  const { vendorId, inviteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!vendorId || !inviteId || !token) {
    return (
      <div className="lp3 lp3-auth auth-shell">
        <PublicFonts />
        <div className="auth-card">
          <h1>Invalid invitation link</h1>
          <p className="subtitle">
            This invitation link is incomplete or malformed. Ask the person who invited you to send a new one.
          </p>
        </div>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const acceptInvitation = httpsCallable(functions, 'acceptInvitation');
      const result = await acceptInvitation({
        vendorId, inviteId, token, password,
        fullName: fullName.trim(),
        phone: phone.trim() || undefined,
      });
      // We don't have email back from the function — fetch it indirectly by signing in.
      // For v0 we tell the user to sign in manually after accepting:
      navigate('/login?accepted=1');
      // Optional: if the function returned email, we could auto-sign-in here. Keeping it simple.
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lp3 lp3-auth auth-shell">
      <PublicFonts />
      <form className="auth-card" onSubmit={submit}>
        <h1>Set up your account</h1>
        <p className="subtitle">
          Create a password to start submitting service requests.
        </p>

        <label>Your full name</label>
        <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required autoComplete="name" />

        <label>Phone (optional)</label>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />

        <label>Password (8+ characters)</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />

        <button type="submit" disabled={busy}>{busy ? 'Creating account…' : 'Create account'}</button>
        {error && <div className="error">{error}</div>}

        <div className="footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </form>
    </div>
  );
}

function prettyError(err) {
  const msg = err?.message || '';
  if (msg.includes('already-exists') || msg.includes('already exists')) {
    return 'An account already exists for this email. Try signing in instead.';
  }
  if (msg.includes('expired')) return 'This invitation link has expired. Ask for a new one.';
  if (msg.includes('Invalid invitation token')) return 'Invalid invitation link. Ask for a fresh one.';
  if (msg.includes('used or revoked')) return 'This invitation has already been used or was revoked.';
  return msg || 'Could not create your account.';
}
